import fp from "lodash/fp"
import { DateTime } from "luxon"

import type { TFunction } from "i18next"
import type { Palette } from "@mui/material"
import type { CurveType } from "@mui/x-charts"
import type { DatasetType } from "@mui/x-charts/internals"

import type { MuiLineConfig } from "types/muiLine.types"
import type { RecordType, LinePlotView, RecordWTimestamp } from "types/dashboard.types"
import type { LineFill } from "types/viewProperties.types"
import type { FieldQualityMap } from "types/quality.types"

import { formatXYPlotData } from "helpers/formatters/dataFormatters"
import { getXYPlotBounds } from "helpers/formatters/plots/xyChart"
import {
  axisToFormatterProps,
  buildSeriesColorMap,
  createQualityLineMark,
  getFormatter,
  getSeriesKey,
  getSeriesLabel,
  getMark,
  splitFormatter,
  buildSeriesByKey,
  buildLineFillValueMaps,
  calculateLineFillData,
  getQualityColors,
} from "./helpers"

interface muiLineConfigParams {
  view: LinePlotView
  data: RecordType[]
  tooltipData: RecordType[]
  palette: Palette
  t: TFunction
  locale?: string
  fromDate: number
  toDate: number
  fieldQualityMap?: FieldQualityMap
}

export default function muiLineConfig({
  view,
  data,
  tooltipData,
  palette,
  t,
  locale,
  fromDate,
  toDate,
  fieldQualityMap,
}: muiLineConfigParams): MuiLineConfig {
  const formattedPlotData = formatXYPlotData(data)
  const formattedTooltipData = formatXYPlotData(tooltipData)
  const [[xDomainMin, xDomainMax], [yDomainMin, yDomainMax]] = getXYPlotBounds(
    view,
    formattedPlotData,
    fromDate,
    toDate,
  )
  const viewProperties = view.properties
  const fieldProperties = viewProperties.fieldProperties
  const fallbackColors = viewProperties.colors

  const fill = viewProperties.fill

  const seriesByKey = buildSeriesByKey(formattedPlotData, fill)

  const seriesColorMap = buildSeriesColorMap(seriesByKey, fallbackColors, fieldProperties)

  const xFormatter = splitFormatter(
    getFormatter(axisToFormatterProps(viewProperties.axes.x, viewProperties, locale)),
  )
  const yFormatterProps = axisToFormatterProps(
    viewProperties.axes.y,
    viewProperties,
    locale,
  )
  const yFormatter = getFormatter(yFormatterProps)

  const customXFormatter =
    viewProperties.axes.x.type === "time"
      ? viewProperties.axes.x.valueFormatter
      : undefined
  const finalXFormatter = customXFormatter
    ? (value: Date) => splitFormatter((v: Date) => customXFormatter(v, t))(value)
    : xFormatter

  const customYFormatter =
    viewProperties.axes.y.type === "number"
      ? viewProperties.axes.y.valueFormatter
      : undefined
  const finalYFormatter: (value: number) => string = customYFormatter
    ? (value: number) => customYFormatter(value, t)
    : (value: number) => yFormatter(value)

  // A tooltip has room the axis tick labels do not, so a plot can ask it for more digits.
  const tooltipDecimalPlaces = viewProperties.tooltip?.decimalPlaces
  const tooltipFormatter =
    tooltipDecimalPlaces !== undefined &&
    yFormatterProps.type === "number" &&
    !customYFormatter
      ? (getFormatter({
          ...yFormatterProps,
          decimalPlaces: tooltipDecimalPlaces,
        }) as (value: number) => string)
      : undefined

  const tooltipDataByMillis = fp.flow(
    fp.map((d: RecordWTimestamp) => ({
      ...d,
      _time: DateTime.fromJSDate(d._time as Date).toMillis(),
    })),
    fp.groupBy((d) => d._time),
    fp.mapValues((records) =>
      fp.fromPairs(
        records.map((r) => [
          r._field,
          typeof r._value === "number" ? finalYFormatter(r._value) : undefined,
        ]),
      ),
    ),
  )(formattedTooltipData)

  const fieldsWithShading = fieldProperties
    ? new Set(
        fp.flow(
          fp.entries,
          fp.filter(([_, props]) => props.backgroundShading === true),
          fp.map(([key, _]) => {
            const parsed = JSON.parse(key)
            return parsed._field || key
          }),
        )(fieldProperties),
      )
    : new Set()

  const forecastStartDate: Date | undefined = fieldsWithShading.size
    ? (fp.flow(
        fp.filter((d: RecordWTimestamp) => !!d._field && fieldsWithShading.has(d._field)),
        (records) =>
          fp.minBy((d: RecordWTimestamp) => (d._time as Date).getTime(), records),
      )(formattedPlotData)?._time as Date)
    : undefined

  const forecastEndDate = forecastStartDate ? new Date(xDomainMax) : undefined

  const lineSeries = fp.toPairs(seriesByKey).map(([key, value]) => ({
    type: "line" as const,
    id: key,
    dataKey: key,
    label:
      fieldProperties && fieldProperties[key]?.labelKey
        ? t(fieldProperties[key].labelKey!)
        : getSeriesLabel(value, t),
    showMark: viewProperties.showMarks,
    connectNulls: viewProperties.connectNulls,
    curve: (fieldProperties && fieldProperties[value._field]?.curve
      ? fieldProperties[value._field].curve
      : viewProperties.curve) as CurveType,
    markerSize: 1,
    color: seriesColorMap[key],
    valueFormatter: (value: number | null) =>
      value !== null ? finalYFormatter(value) : null,
  }))

  // Create line fill series for filling between two lines
  const lineFillSeries = viewProperties.lineFills
    ? viewProperties.lineFills.flatMap((lineFill: LineFill, index: number) => {
        const keyA = fp.find(
          (key: string) => seriesByKey[key]?._field === lineFill.lineA,
          fp.keys(seriesByKey),
        )
        const keyB = fp.find(
          (key: string) => seriesByKey[key]?._field === lineFill.lineB,
          fp.keys(seriesByKey),
        )

        if (!keyA || !keyB) {
          return []
        }

        const stackId = `fill_stack_${index}`
        // Create two series: base (min) with area, and diff (max-min) stacked on top
        return [
          {
            type: "line" as const,
            id: `fill_${index}_base`,
            dataKey: `fill_${index}_base`,
            label: "", // Hidden in legend
            showMark: false,
            connectNulls: viewProperties.connectNulls,
            curve: viewProperties.curve as CurveType,
            area: true,
            stack: stackId,
            color: "transparent",
            valueFormatter: () => null,
          },
          {
            type: "line" as const,
            id: `fill_${index}_diff`,
            dataKey: `fill_${index}_diff`,
            label: "",
            showMark: false,
            connectNulls: viewProperties.connectNulls,
            curve: viewProperties.curve as CurveType,
            area: true,
            stack: stackId,
            color: lineFill.color || seriesColorMap[keyA],
            valueFormatter: () => null,
          },
        ]
      })
    : []

  const series = [...lineSeries, ...lineFillSeries]

  const defaultNullValues = fp.flow(
    fp.map((key: string) => [key, null]),
    fp.fromPairs,
  )(fp.keys(seriesByKey))

  const formattedData: DatasetType = fp.flow(
    fp.groupBy((item: RecordWTimestamp) => item._time),
    fp.map((recordsAtSameTime: RecordWTimestamp[]) => {
      const time = recordsAtSameTime[0]._time
      const fieldsAtTime = fp.flow(
        fp.map((item: RecordWTimestamp) => ({
          [getSeriesKey(item, fill)]: item._value,
        })),
        fp.assignAll,
      )(recordsAtSameTime)

      // Build value maps once for all line fills before processing
      const lineFillValueMaps = viewProperties.lineFills
        ? buildLineFillValueMaps(
            viewProperties.lineFills,
            seriesByKey,
            formattedPlotData,
            fill,
          )
        : []

      // Calculate line fill data for stacked area between two lines
      const lineFillData = viewProperties.lineFills
        ? calculateLineFillData(
            viewProperties.lineFills,
            seriesByKey,
            fieldsAtTime,
            time,
            lineFillValueMaps,
          )
        : {}

      return {
        ...defaultNullValues,
        ...fieldsAtTime,
        ...lineFillData,
        _time: time,
      }
    }),
    fp.sortBy((d) => d._time.getTime()),
  )(formattedPlotData)

  const axisColor = palette.neutral[600]

  const xAxis = [
    {
      id: viewProperties.axes.x.key,
      dataKey: viewProperties.axes.x.key,
      scaleType: viewProperties.axes.x.scaleType,
      height: 35,
      valueFormatter: finalXFormatter,
      tickLabelStyle: {
        fill: axisColor,
        fontWeight: 500,
        fontSize: 10,
      },
      tickMinStep: viewProperties.axes.x.totalTicks
        ? (xDomainMax - xDomainMin) / viewProperties.axes.x.totalTicks
        : undefined,
      domainLimit: () => ({
        min: xDomainMin,
        max: xDomainMax,
      }),
    },
  ]

  const yAxis = [
    {
      scaleType: viewProperties.axes.y.scaleType,
      width: 65,
      valueFormatter: finalYFormatter,
      tickLabelStyle: {
        fill: axisColor,
        fontWeight: 500,
        fontSize: 10,
      },
      tickMinStep: viewProperties.axes.y.totalTicks
        ? (yDomainMax - yDomainMin) / viewProperties.axes.y.totalTicks
        : undefined,
      domainLimit: () => ({
        min: yDomainMin,
        max: yDomainMax,
      }),
    },
  ]

  const tooltip = viewProperties.tooltip
  const margin = viewProperties.margin
  const grid = viewProperties.grid
  const gridStyle = viewProperties.gridStyle ?? {
    stroke: palette.neutral[900],
    opacity: 0.05,
    strokeDasharray: "3, 3",
  }
  const xReferenceLines = viewProperties.xReferenceLines
  const yReferenceLines = viewProperties.yReferenceLines
  const yReferenceStepLine = viewProperties.yReferenceStepLine
  const legend = viewProperties.legend
  const qualityColors = getQualityColors(palette)
  const mark = fieldQualityMap
    ? createQualityLineMark(fieldQualityMap, formattedData, qualityColors)
    : getMark(viewProperties.mark)
  const lineWidth = viewProperties.lineWidth

  return {
    series,
    formattedData,
    tooltipDataByMillis,
    xAxis,
    yAxis,
    tooltip,
    tooltipFormatter,
    margin,
    grid,
    gridStyle,
    xReferenceLines,
    yReferenceLines,
    yReferenceStepLine,
    legend,
    mark,
    axisColor,
    lineWidth,
    xDomainMin,
    xDomainMax,
    forecastStartDate,
    forecastEndDate,
  }
}
